import colors from './colors';
import fonts from './fonts';
import layouts from './layouts';
import mediaQueries from './mediaQueries';
import mixins from './mixins';
import zIndices from './zIndices';

const theme = {
  colors,
  fonts,
  layouts,
  mediaQueries,
  mixins,
  zIndices,

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    outline: '0 0 0 3px rgba(236, 72, 153, 0.5)',
    none: 'none',
    // Design System shadows (from ItemManagement)
    subtle: '0 1px 2px rgba(0, 0, 0, 0.05)',
    elevated: '0 2px 4px rgba(0, 0, 0, 0.08)',
    hover: '0 4px 6px rgba(0, 0, 0, 0.1)',
    dropdown: '0 10px 25px rgba(0, 0, 0, 0.2)',
    modal: '0 20px 25px rgba(0, 0, 0, 0.1)',
    // The one card shadow checkout uses (#544): a hairline plus a soft, warm
    // spread. Warm because the page behind it is the brand gold, and a neutral
    // grey shadow reads dirty on it.
    card: '0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(58, 42, 30, 0.06)',
  },
  transitions: {
    default: 'all 0.3s ease',
    fast: 'all 0.15s ease',
    slow: 'all 0.5s ease',
    quick: 'all 0.2s ease',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
  },
  radii: {
    sm: '6px',
    md: '8px',
    control: '10px', // inputs, slots, secondary buttons (#544)
    lg: '12px',
    full: '9999px',
  },
};

/**
 * The checkout scale (#544).
 *
 * The root font-size is 10px (src/index.css), and every value here is in
 * those rem units — 1.6rem is 16px. The checkout components used to write
 * `1rem` and `0.875rem` as if the root were 16px, so their body text rendered
 * at 10px and 8.75px on desktop. That, not the layout, is why checkout read
 * small. Components take their sizes from here and nowhere else; the theme
 * test pins the floor.
 *
 * Playfair (fonts.primary) for headings and totals, Work Sans (fonts.secondary)
 * for everything else. Brick red is the one primary action colour in checkout;
 * green is reserved for the confirmed state.
 */
theme.checkout = {
  text: {
    body: {
      fontFamily: fonts.secondary,
      fontSize: '1.6rem',
      lineHeight: '1.5',
      fontWeight: 400,
    },
    label: {
      fontFamily: fonts.secondary,
      fontSize: '1.4rem',
      lineHeight: '1.4',
      fontWeight: 600,
    },
    helper: {
      fontFamily: fonts.secondary,
      fontSize: '1.4rem',
      lineHeight: '1.4',
      fontWeight: 400,
    },
    caption: {
      fontFamily: fonts.secondary,
      fontSize: '1.3rem',
      lineHeight: '1.2',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    // Option cards, summary items, the pickup line.
    title: {
      fontFamily: fonts.secondary,
      fontSize: '1.8rem',
      lineHeight: '1.3',
      fontWeight: 700,
    },
    // Section headings inside a card: "How would you like your order?"
    heading: {
      fontFamily: fonts.primary,
      fontSize: '2.6rem',
      lineHeight: '1.15',
      fontWeight: 700,
    },
    // The page title above the stepper.
    pageTitle: {
      fontFamily: fonts.primary,
      fontSize: '2.8rem',
      lineHeight: '1.1',
      fontWeight: 700,
    },
    total: {
      fontFamily: fonts.primary,
      fontSize: '2.2rem',
      lineHeight: '1.2',
      fontWeight: 700,
    },
    totalAmount: {
      fontFamily: fonts.primary,
      fontSize: '2.4rem',
      lineHeight: '1.2',
      fontWeight: 700,
    },
  },
  control: {
    height: '5.2rem', // inputs
    slotHeight: '4.8rem', // pickup time slots, tip buttons
    primaryHeight: '6rem', // Continue / Place Order
  },
  card: {
    padding: '3.2rem',
    paddingMobile: '2rem',
    gap: '3.2rem', // between sections inside the step card
  },
  radius: {
    card: theme.radii.lg,
    control: theme.radii.control,
  },
  shadow: theme.shadows.card,
  primary: colors.primary,
};

export default theme;
